import { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { Button, Logo, LanguageSwitcher } from '@core/ui';
import { APP_CONTENT_ID, SIDEBAR_ID } from './appShell';
import { SIDEBAR_SHORTCUT_LABEL, useSidebarCollapsed } from './useSidebarCollapsed';
import { logout, selectCurrentUser, useLogoutMutation } from '@features/auth';
import { OrganizationSwitcher } from '@features/organization';

interface NavItem {
  to: string;
  labelKey: string;
  icon: string;
}

/**
 * Primary navigation — the sidebar on md+ and the bottom tab bar on mobile.
 *
 * Each item is a real operator surface; nothing is listed
 * before its page exists, so no nav item is ever a dead link.
 */
const NAV_ITEMS: NavItem[] = [
  { to: '/conversations', labelKey: 'nav.conversations', icon: 'heroicons:chat-bubble-left-right' },
  { to: '/cancellations', labelKey: 'nav.cancellations', icon: 'heroicons:arrow-path-rounded-square' },
];

/**
 * A tab bar with one tab navigates nowhere — it is a label taking a permanent
 * bite out of a phone screen. It appears with the second destination.
 */
const SHOW_TAB_BAR = NAV_ITEMS.length > 1;

/**
 * The label of a collapsed rail item, shown next to the icon on hover / keyboard
 * focus.
 *
 * It is `aria-hidden` on purpose: the real label stays in the link as `sr-only`
 * text, so a screen reader announces it once and a mouse or keyboard user still
 * gets to read what the icon means. A `title` attribute would do neither — it
 * never fires on focus, and it is not an accessible name.
 */
function RailTooltip({ label }: { label: string }) {
  return (
    <span
      aria-hidden="true"
      className={[
        'pointer-events-none absolute left-full top-1/2 z-40 ml-2 -translate-y-1/2',
        'whitespace-nowrap rounded-lg px-2 py-1 text-xs font-medium shadow-sm',
        'bg-[var(--color-accent)] text-white',
        'opacity-0 transition-opacity duration-100 motion-reduce:transition-none',
        'group-hover:opacity-100 group-focus-visible:opacity-100',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

/**
 * Authenticated application shell.
 *
 * - md+ : top bar + left sidebar + content. The sidebar collapses to an icon
 *   rail (see `useSidebarCollapsed`); the choice is remembered.
 * - mobile : top bar + content + a fixed bottom tab bar (thumb-friendly),
 *   with the content padded (`pb-24 md:pb-8`) so it clears the bar. There is no
 *   collapse there — the tab bar is already the compact form.
 *
 * Re-initializes Preline's JS components on route changes.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const [serverLogout, { isLoading: loggingOut }] = useLogoutMutation();
  const [collapsed, toggleSidebar] = useSidebarCollapsed();

  useEffect(() => {
    window.HSStaticMethods?.autoInit();
  }, [location.pathname]);

  const handleLogout = async () => {
    // Best-effort server-side token revocation before clearing client state
    // (the request still carries the token at this point).
    try {
      await serverLogout().unwrap();
    } catch {
      /* ignore — log out locally regardless */
    }
    dispatch(logout());
    navigate('/login');
  };

  // `group` + `relative` are what the collapsed rail's tooltip hangs off; the
  // active pill stays a background on the icon, so "where am I" survives the
  // label going away.
  const sidebarLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'group relative flex items-center min-h-[40px] rounded-lg text-sm font-medium transition-colors',
      'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]',
      collapsed ? 'w-11 justify-center' : 'w-full gap-x-3 px-3 py-2',
      isActive
        ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
        : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-text)]',
    ].join(' ');

  const tabLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'flex flex-1 flex-col items-center justify-center gap-y-0.5 py-2 min-h-[52px] text-[11px] font-medium transition-colors',
      'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent)]',
      isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-muted)]',
    ].join(' ');

  return (
    // The id is the handle a modal overlay uses to mark the whole shell `inert`
    // while it is open (see `@core/layouts/appShell`).
    <div id={APP_CONTENT_ID} className="min-h-screen flex flex-col bg-[var(--color-bg-subtle)]">
      {/* Top bar. `pt-[env(safe-area-inset-top)]` drops the content below the
          status bar / notch on mobile; the surface bg + border fill the inset so
          the strip above the logo reads as part of the header, not a gap. */}
      <header className="sticky top-0 z-30 shrink-0 bg-[var(--color-surface)] border-b border-[var(--color-border)] pt-[env(safe-area-inset-top)]">
        <div className="h-16 flex items-center justify-between gap-x-2 px-4 sm:px-6">
          <Logo className="h-7 w-auto shrink-0" />
          <div className="flex items-center gap-x-2 sm:gap-x-3 min-w-0">
            {user && (
              <span
                title={user.email}
                className="hidden lg:inline text-sm text-[var(--color-text-secondary)] truncate max-w-[220px]"
              >
                {user.email}
              </span>
            )}
            <OrganizationSwitcher />
            <LanguageSwitcher compact />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              disabled={loggingOut}
              aria-label={t('nav.logout')}
              className="min-w-[44px]"
            >
              <Icon icon="heroicons:arrow-right-on-rectangle" className="h-4 w-4" />
              <span className="hidden sm:inline">{t('nav.logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar (md+). Collapsed it is a 64px icon rail; the width animates so
            the content reflow reads as one movement instead of a jump.

            `sticky` + a viewport-tall box (minus the sticky header) is what keeps
            the nav — and the collapse toggle pinned to its bottom edge — on screen
            while a long inbox scrolls. Stretched to the page height instead, the
            toggle would sit hundreds of pixels below the fold. */}
        <aside
          id={SIDEBAR_ID}
          className={[
            'hidden md:flex flex-col shrink-0 bg-[var(--color-surface)] border-r border-[var(--color-border)]',
            'sticky self-start top-[calc(4rem+env(safe-area-inset-top))]',
            'h-[calc(100vh-4rem-env(safe-area-inset-top))]',
            'transition-[width,padding] duration-200 ease-out motion-reduce:transition-none',
            collapsed ? 'md:w-16 p-2' : 'md:w-60 p-4',
          ].join(' ')}
        >
          <nav className={`flex flex-col gap-y-1 ${collapsed ? 'items-center' : ''}`} aria-label={t('nav.primary')}>
            {/* No `end`: an open conversation (`/conversations/:uuid`) is still
                the conversations surface, and the nav must keep saying so. */}
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} className={sidebarLinkClass}>
                <Icon icon={item.icon} className="h-5 w-5 shrink-0" aria-hidden="true" />
                {/* The label never leaves the DOM — collapsing hides it from eyes,
                    not from the accessibility tree, so the link keeps its name. */}
                <span className={collapsed ? 'sr-only' : 'flex-1 truncate'}>{t(item.labelKey)}</span>
                {collapsed && <RailTooltip label={t(item.labelKey)} />}
              </NavLink>
            ))}
          </nav>

          {/* The toggle sits at the bottom edge, out of the nav's tab order path:
              it is chrome, used once a session, not a destination. */}
          <div
            className={[
              'mt-auto pt-2 flex border-t border-[var(--color-border)]',
              collapsed ? 'justify-center' : 'justify-end',
            ].join(' ')}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              aria-expanded={!collapsed}
              aria-controls={SIDEBAR_ID}
              aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
              title={t('nav.sidebarShortcutHint', {
                action: collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar'),
                shortcut: SIDEBAR_SHORTCUT_LABEL,
              })}
              className="px-2 min-w-[40px]"
            >
              <Icon
                icon={collapsed ? 'heroicons:chevron-double-right' : 'heroicons:chevron-double-left'}
                className="h-5 w-5 shrink-0"
                aria-hidden="true"
              />
            </Button>
          </div>
        </aside>

        {/* Content. `min-w-0` lets wide children shrink to the viewport instead of
            forcing horizontal overflow on mobile; the bottom padding clears the
            fixed tab bar (plus the home-indicator safe area). */}
        <main
          className={[
            'flex-1 min-w-0 p-4 sm:p-6 lg:p-8',
            SHOW_TAB_BAR ? 'pb-24 sm:pb-24 md:pb-8 lg:pb-8' : 'pb-8',
          ].join(' ')}
        >
          <Outlet />
        </main>
      </div>

      {/* Bottom tab bar (mobile only), once there is more than one destination. */}
      {SHOW_TAB_BAR && (
        <nav
          className="md:hidden fixed bottom-0 inset-x-0 z-40 flex bg-[var(--color-surface)] border-t border-[var(--color-border)] pb-[env(safe-area-inset-bottom)]"
          aria-label={t('nav.primary')}
        >
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className={tabLinkClass}>
              <Icon icon={item.icon} className="h-5 w-5" aria-hidden="true" />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
